import type { TFunction } from "i18next";
import type { BillOut } from "../../../../../api/bills";
import {
  averageLinePercentOf,
  customerDiscountOf,
  formatSignedBillPercent,
} from "../../billDiscount";
import { appliedBillDiscount } from "../discounts/billAmountDiscount";
import { attachedDepositOf, depositTitleOf } from "../deposits/billDeposit";

/**
 * Нещата за сметката върху кутийката на масата в „Списък" (`TableList`):
 * резервация, брой гости, „Карта — без бон", депозит, клиент, % по продукти и
 * отстъпката в € на цялата сметка. Само показване — всичко е от документа на
 * сметката през съществуващите helper-и, нищо не се смята наново. Фактите са
 * само низове и числа: влизат в ключа за мерене на кутийката, а къде и в какъв
 * вид стоят (текст, стойност, иконка, „+N"), решава `planCardFacts`.
 */
export type TableCardFactKind =
  | "reservation"
  | "guests"
  | "cardNoReceipt"
  | "deposit"
  | "client"
  | "lineDiscount"
  | "billDiscount";

export interface TableCardFact {
  kind: TableCardFactKind;
  /** Стойността — само за нещата, които мърдат сумата („-50%", „-3.00 €");
   *  null — фактът започва с иконката си. */
  value: string | null;
  /** Броят гости — числото до иконката (то е и стойността им). */
  count?: number;
  /** Текстът на реда след иконката/стойността; null — фактът няма ред. */
  text: string | null;
  /** Сумата на депозита пред текста на реда — не се реже („50.00 €"). */
  textAmount?: string;
  /** Текстът е име (клиент, резервация, депозит) — реже се с „…", но не под
   *  `MIN_TEXT`; общите етикети („на цялата сметка", „4 гости") — никога. */
  textIsName: boolean;
  /** Подсказката — цялото нещо с думи. */
  title: string;
}

/** Редът на четене (отляво надясно, отгоре надолу): хората към името, парите
 *  към сумата — както в сметката: проценти, после € от цялото, после сумата. */
const FACT_READING_ORDER: readonly TableCardFactKind[] = [
  "reservation",
  "guests",
  "cardNoReceipt",
  "deposit",
  "client",
  "lineDiscount",
  "billDiscount",
];

/** Кой отива първи в „+N", щом няма място дори за всички иконки. */
export const FACT_HIDE_ORDER: readonly TableCardFactKind[] = [
  "guests",
  "reservation",
  "deposit",
  "lineDiscount",
  "client",
  "billDiscount",
  "cardNoReceipt",
];

/** Кой остава на свой ред, щом височината не стига за всички — другите
 *  слизат до сумата. */
export const FACT_TEXT_ORDER: readonly TableCardFactKind[] = [
  "cardNoReceipt",
  "client",
  "reservation",
  "deposit",
  "billDiscount",
  "lineDiscount",
  "guests",
];

export interface TableCardFactsContext {
  t: TFunction;
  /** Името на клиента от справочника — само ако документът го няма. */
  clientLabel: string | null;
  /** Гостите на настанената резервация (`describe` — null в локален режим). */
  reservationGuestName: string | null;
  /** Локален режим срещу касата (docs/edge.md). */
  edgeLocal: boolean;
  /** Отстъпката в € е заключена — локален режим с каса, която НЕ я умее
   *  (`useBillDiscountLocked`); каса, която я умее, я прилага и без интернет. */
  billDiscountLocked: boolean;
  isClosedEmpty: boolean;
  isClosing: boolean;
}

/** „50.00" → „50.00 €" — тесен неразделим интервал пред знака. */
const euroOf = (amount: string) => `${amount}\u202F€`;

const reservationFactOf = (name: string | null, t: TFunction): TableCardFact | null =>
  name
    ? {
        kind: "reservation",
        value: null,
        text: name,
        textIsName: true,
        title: t("ordering.seatedReservationTag", { name }),
      }
    : null;

// Броят гости — винаги, щом е въведен (както в режим „Схема"), независимо от
// настройката „Въвеждане на брой гости".
const guestsFactOf = (bill: BillOut, t: TFunction): TableCardFact | null => {
  const count = bill.guests ?? 0;
  if (count <= 0) return null;
  return {
    kind: "guests",
    value: null,
    count,
    text: t("reservations.counts.guests", { count }),
    textIsName: false,
    title: t("ordering.cardInfo.guestsTitle", { count }),
  };
};

const cardNoReceiptFactOf = (bill: BillOut, t: TFunction): TableCardFact | null => {
  if (!bill.card_payment_pending) return null;
  const label = t("ordering.cardNoReceiptTag");
  return { kind: "cardNoReceipt", value: null, text: label, textIsName: false, title: label };
};

// Депозитът — само на отворената сметка (`attachedDepositOf`); без име —
// номерът му („№ 12").
const depositFactOf = (bill: BillOut, t: TFunction): TableCardFact | null => {
  const deposit = attachedDepositOf(bill);
  if (!deposit) return null;
  const amount = euroOf(deposit.amount);
  return {
    kind: "deposit",
    value: null,
    text: deposit.name || t("ordering.cardInfo.depositNumber", { id: deposit.id }),
    textAmount: amount,
    textIsName: true,
    title: t("ordering.cardInfo.depositTitle", { title: depositTitleOf(t, deposit), amount }),
  };
};

// Клиентът (и с 0% — тогава започва с иконката). Името е от документа на
// сметката; справочникът е само резерва (кешът му е асинхронен).
const clientFactOf = (
  bill: BillOut,
  clientLabel: string | null,
  t: TFunction,
): TableCardFact | null => {
  const customer = customerDiscountOf(bill);
  if (!customer) return null;
  const name = bill.discount_applied?.client_name || clientLabel || "";
  const percent = formatSignedBillPercent(customer.percent);
  return {
    kind: "client",
    value: customer.percent !== 0 ? percent : null,
    text: name || null,
    textIsName: true,
    title: t("ordering.cardInfo.clientTitle", { name, percent }),
  };
};

// Средният % по продуктите — само когато клиентът няма процент (няма клиент
// или е с 0%): процентът на клиента вече е в `total_discount`.
const lineDiscountFactOf = (bill: BillOut, t: TFunction): TableCardFact | null => {
  if ((customerDiscountOf(bill)?.percent ?? 0) !== 0) return null;
  const average = averageLinePercentOf(bill);
  if (average === 0) return null;
  const percent = formatSignedBillPercent(average);
  return {
    kind: "lineDiscount",
    value: percent,
    text: t("ordering.cardInfo.lineDiscount"),
    textIsName: false,
    title: t(
      average > 0 ? "ordering.cardInfo.lineDiscountTitle" : "ordering.cardInfo.lineSurchargeTitle",
      { percent },
    ),
  };
};

const billDiscountFactOf = (bill: BillOut, t: TFunction): TableCardFact | null => {
  const discount = appliedBillDiscount(bill);
  if (discount <= 0) return null;
  const value = `-${euroOf(discount.toFixed(2))}`;
  return {
    kind: "billDiscount",
    value,
    text: t("ordering.cardInfo.billDiscount"),
    textIsName: false,
    title: t("ordering.cardInfo.billDiscountTitle", { amount: value }),
  };
};

/**
 * Фактите на една сметка в реда на четене (`FACT_READING_ORDER`). При
 * „Закрива се" — нищо (долният ред е само етикетът, както досега); закритата
 * празна — само „Карта — без бон". В локален режим касата не прилага
 * резервацията и депозита, а € отстъпката — само ако не я умее; фактът би
 * излъгал, затова тогава ги няма.
 */
export const tableCardFactsOf = (
  bill: BillOut,
  {
    t,
    clientLabel,
    reservationGuestName,
    edgeLocal,
    billDiscountLocked,
    isClosedEmpty,
    isClosing,
  }: TableCardFactsContext,
): TableCardFact[] => {
  if (isClosing) return [];
  const candidates = isClosedEmpty
    ? [cardNoReceiptFactOf(bill, t)]
    : [
        reservationFactOf(edgeLocal ? null : reservationGuestName, t),
        guestsFactOf(bill, t),
        cardNoReceiptFactOf(bill, t),
        edgeLocal ? null : depositFactOf(bill, t),
        clientFactOf(bill, clientLabel, t),
        lineDiscountFactOf(bill, t),
        billDiscountLocked ? null : billDiscountFactOf(bill, t),
      ];
  return candidates
    .filter((fact): fact is TableCardFact => fact !== null)
    .sort((a, b) => FACT_READING_ORDER.indexOf(a.kind) - FACT_READING_ORDER.indexOf(b.kind));
};
